// import './App.css';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from './v3/hooks/useQuery';
import { ToastContainer, cssTransition } from 'react-toastify';
import { ThemeProvider } from 'styled-components';

// v3
import Deals from './v3/pages/Deals';
import Flow from './v3/templates/Flow';
import Welcome from './v3/pages/Welcome';
import ZipcodeUnavailable from './v3/pages/ZipcodeUnavailable';
import ReviewOfferWaitlist from './v3/pages/ReviewOfferWaitlist';
import ChangePaymentMethod from './v3/pages/ChangePaymentMethod';
import { ChangePaymentMethodSucceeded } from './v3/pages/ChangePaymentMethod/success';

import { setLeadData } from './leadData/leadSlice';
import { handleErrorMessage } from './utils/errorHandler';
import { ContractFlowClient } from './api/clients/ContractFlowClient';
import ConnectBankAccount from './pages/ConnectBankAccount';
import ConnectCreditCard from './pages/ConnectCreditCard';
import CreditCardSuccess from './pages/CreditCardSuccess';
import Microdeposits from './pages/Microdeposits';
import PaymentSuccess from './pages/PaymentSuccess';
import OfferSimulator from './pages/OfferSimulator/OfferSimulator';

import { MixpanelProvider } from './v3/contexts/MixpanelContext';
import { useHotjar } from './hooks/useHotjar';
import { useAnalytics } from './hooks/useAnalytics';
import theme from 'styles/theme';
import GlobalStyles from 'styles/global';

import 'react-toastify/dist/ReactToastify.css';
import 'antd/dist/antd.css';
import { emitter } from '@marvelapp/react-ab-test';

import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// const SPECIAL_PAGE_MAPPER = {
//   3: '/welcome',
// };

import * as Sentry from "@sentry/react";
import * as SentryBrowser from "@sentry/browser";
// import * as Sentry from "@sentry/react";
// import * as SentryBrowser from "@sentry/browser";
// import { Integrations } from "@sentry/tracing";
// import { Replay } from "@sentry/replay";
// import { BrowserTracing } from "@sentry/tracing";
// import { Replay } from "@sentry/replay";

import Clarity from '@microsoft/clarity';

// Stripe reads the locale from the browser when it is not told one, so the Financial
// Connections modal came up in Portuguese on a pt-BR device inside an English app. Pin it.
//
// Built once at module load rather than per render: loadStripe returns a new promise for each
// distinct argument list, and re-creating it on every render would re-mount Elements.
//
// An ad-blocker or a network drop stops Stripe.js loading; resolve to null (Elements accepts it,
// the card forms bail on a missing `stripe`) instead of an unhandled rejection.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY, { locale: 'en' }).catch(() => null);

SentryBrowser.browserTracingIntegration
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
// if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      // Enables automatic instrumentation for browser monitoring, including errors and performance
      SentryBrowser.browserTracingIntegration(),
      // Enables session replay
      SentryBrowser.replayIntegration(),
    ],

    sendDefaultPii: true,
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // You can remove this option if you're not planning to use Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want this to be 100% while seeing how it works.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the session, capture the session replay when an error occurs.
  });
// }

// only record production hostname
if (['contractflow.savings.club'].includes(window.location.host)) {
  Clarity.init('q6b70flnu6');
}

emitter.defineVariants('Plaid On Exit', ['A', 'B'], [50, 50]);

function App() {
  const query = useQuery();
  const cfTokenQuery = query.get('cfUuid');
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentStep } = useSelector((state) => state.lead);
  useHotjar();
  useAnalytics();

  const handleRedirect = useCallback(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
    // if (Object.keys(SPECIAL_PAGE_MAPPER).includes(String(currentStep))) {
    //   navigate(SPECIAL_PAGE_MAPPER[currentStep]);
    // }
  }, []);

  useEffect(() => {
    const shouldRedirect = pathname === '/';

    if (shouldRedirect) {
      handleRedirect(currentStep);
    }
  }, [handleRedirect, currentStep, pathname]);

  useEffect(() => {
    if (cfTokenQuery) {
      async function getInfo() {
        const [data, error] = await ContractFlowClient().getContractFlowInfo(
          cfTokenQuery
        );

        // A failed load leaves no data to read: report it and stop, rather than
        // destructuring `undefined` on the next line.
        if (!data) {
          handleErrorMessage(error);
          return;
        }

        const { contractFlowStep, ...leadData } = data;
        dispatch(setLeadData({ currentStep: contractFlowStep, leadData }));
      }

      getInfo();
    }
  }, [cfTokenQuery, dispatch]);

  const customTransition = cssTransition({
    enter: 'slide-in-bottom',
    exit: 'slide-out-bottom',
  });

  return (
    <>
      <MixpanelProvider>
        <ThemeProvider theme={theme}>
          <Elements stripe={stripePromise}>
            <GlobalStyles />
            <ToastContainer
              enableMultiContainer
              transition={customTransition}
              containerId="customPosition"
              className="customPosition"
            />
            <ToastContainer enableMultiContainer />
            <Routes>
              <Route path="/" element={<Flow />} />
              <Route path="/change-payment-method/:cfUuid" element={<ChangePaymentMethod />} />
              <Route path="/change-payment-method/:cfUuid/success" element={<ChangePaymentMethodSucceeded />} />
              <Route path="/deals" element={<Deals />} />
              <Route path="/zip-unavailable" element={<ZipcodeUnavailable />} />
              <Route path="/waitlist" element={<ReviewOfferWaitlist />} />
              <Route
                path="/connect-bank-account"
                element={<ConnectBankAccount />}
              />
              <Route
                path="/connect-credit-card"
                element={<ConnectCreditCard />}
              />
              <Route
                path="/credit-card-success"
                element={<CreditCardSuccess />}
              />
              <Route path="/microdeposits" element={<Microdeposits />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/simulator" element={<OfferSimulator />} />
            </Routes>
          </Elements>
        </ThemeProvider>
      </MixpanelProvider>
    </>
  );
}
export default App;
